// src/components/PromptEditorPanel.jsx
// The tenant prompt editor. P0.17 task 17.10b.
//
// Lives inside Profile & Settings → AI Personas, next to the persona NAMES,
// because renaming Victor and rewriting what Victor says are the same question
// asked at two depths.
//
// WHAT THIS SCREEN HAS TO GET RIGHT
// An edit here changes what every user of the tenant gets from every persona.
// So the UI never hides which version is live: each prompt says whether it is
// customised, the editor shows the default alongside, and Reset is always one
// click away. The API is the real gate (requireRole + variable validation); this
// panel refuses to be the only thing standing between a typo and production.

import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import './PromptEditorPanel.css';

const PERSONA_ICON = {
  victor: '♟️', selma: '🔭', cyrano: '✍️', quinn: '📅', jarvis: '🤖', system: '⚙️',
};

export default function PromptEditorPanel() {
  const [prompts, setPrompts]   = useState(null);   // null = loading
  const [error, setError]       = useState('');
  const [openSlug, setOpenSlug] = useState(null);
  const [draft, setDraft]       = useState('');
  const [saving, setSaving]     = useState(false);
  const [notice, setNotice]     = useState('');
  const [saveError, setSaveError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await apiFetch('/api/tenant/prompts');
      const j = await r.json();
      if (!r.ok || !j.success) throw new Error(j.error || `HTTP ${r.status}`);
      setPrompts(j.prompts || []);
    } catch (e) {
      setPrompts([]);
      setError(e.message || 'Could not load prompts');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = (p) => {
    setOpenSlug(p.slug);
    setDraft(p.body);
    setNotice('');
    setSaveError('');
  };

  const close = () => { setOpenSlug(null); setDraft(''); setSaveError(''); };

  const current = prompts?.find(p => p.slug === openSlug) || null;
  const dirty = !!current && draft !== current.body;

  async function save() {
    if (!current) return;
    setSaving(true); setSaveError(''); setNotice('');
    try {
      const r = await apiFetch('/api/tenant/prompts', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: current.slug, body: draft }),
      });
      const j = await r.json();
      if (!r.ok || !j.success) throw new Error(j.error || `HTTP ${r.status}`);
      setNotice(j.notice || 'Saved.');
      await load();
    } catch (e) {
      // The API's message names the offending {variable} and lists the valid
      // ones, so it is shown verbatim rather than replaced with "Save failed".
      setSaveError(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  async function reset(p) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Reset ${p.title} to the default? Your edit will be discarded.`)) return;
    setSaving(true); setSaveError(''); setNotice('');
    try {
      const r = await apiFetch(`/api/tenant/prompts?slug=${encodeURIComponent(p.slug)}`, { method: 'DELETE' });
      const j = await r.json();
      if (!r.ok || !j.success) throw new Error(j.error || `HTTP ${r.status}`);
      setNotice(j.notice || 'Reset to the default.');
      await load();
      if (openSlug === p.slug) close();
    } catch (e) {
      setSaveError(e.message || 'Could not reset');
    } finally {
      setSaving(false);
    }
  }

  if (prompts === null) {
    return <div className="pe-status">Loading prompts…</div>;
  }

  if (error) {
    return <div className="pe-status pe-status--error">{error}</div>;
  }

  if (prompts.length === 0) {
    // The honest empty state. The table is seeded from prompts/*.md by a script,
    // so "none yet" means the deployment has not been seeded — not that
    // something is broken, and not something this screen can fix.
    return (
      <div className="pe-status">
        No prompts are available to edit yet. They are published with a deployment;
        once published they appear here.
      </div>
    );
  }

  return (
    <div className="pe-root">
      <div className="pe-intro">
        Each persona works from a written brief. Edit one to change how it speaks and
        what it prioritises for everyone in your organisation. Changes apply within a
        minute.
      </div>

      {notice && <div className="pe-notice">{notice}</div>}

      <div className="pe-list">
        {prompts.map(p => (
          <div key={p.slug} className={`pe-item ${p.customised ? 'pe-item--custom' : ''}`}>
            <div className="pe-item-head">
              <span className="pe-icon">{PERSONA_ICON[p.persona] || '📝'}</span>
              <div className="pe-item-titles">
                <div className="pe-item-title">
                  {p.title}
                  {p.customised && <span className="pe-badge">Customised</span>}
                  {p.defaultMovedOn && (
                    <span className="pe-badge pe-badge--warn" title="The standard version has been updated since you edited this.">
                      Default updated
                    </span>
                  )}
                </div>
                {p.description && <p className="pe-item-desc">{p.description}</p>}
              </div>
              <div className="pe-item-actions">
                <button type="button" className="pe-btn" onClick={() => (openSlug === p.slug ? close() : open(p))}>
                  {openSlug === p.slug ? 'Close' : 'Edit'}
                </button>
                {p.customised && (
                  <button type="button" className="pe-btn pe-btn--ghost" disabled={saving} onClick={() => reset(p)}>
                    Reset
                  </button>
                )}
              </div>
            </div>

            {openSlug === p.slug && (
              <div className="pe-editor">
                {p.variables?.length > 0 && (
                  <div className="pe-vars">
                    <span className="pe-vars-label">Available placeholders — copy them exactly:</span>
                    <div className="pe-vars-list">
                      {/* B85 — the NAME and what it renders as, for this tenant,
                          right now. The list used to show names alone, so an
                          owner could not tell a placeholder that was about to
                          render correctly from one about to render EMPTY.

                          Three states, styled apart because they mean different
                          things: a value, a gap the owner can close, and a value
                          that only exists per request. */}
                      {p.variables.map((v) => {
                        const r = p.variableValues?.[v] || { state: 'deferred' };
                        return (
                          <span key={v} className={`pe-var-pair pe-var--${r.state}`}>
                            <code className="pe-var">{`{${v}}`}</code>
                            {r.state === 'resolved' && (
                              <span className="pe-var-value" title={r.value}>{r.value}</span>
                            )}
                            {r.state === 'neutral' && (
                              /* B85 — companyName() and readerName() never render
                                 empty: they fall back to "Your Company" and
                                 "there". Correct for a prompt, wrong for this
                                 panel — an owner would otherwise see a plausible
                                 value and never learn it was a placeholder. */
                              <span className="pe-var-value pe-var-value--neutral">
                                {r.value} <em>(placeholder — not set)</em>
                              </span>
                            )}
                            {r.state === 'blank' && (
                              <span className="pe-var-value pe-var-value--blank">not set</span>
                            )}
                            {r.state === 'deferred' && (
                              <span className="pe-var-value pe-var-value--deferred">per account</span>
                            )}
                          </span>
                        );
                      })}
                    </div>
                    <p className="pe-vars-hint">
                      A placeholder that is not on this list is rejected when you save. One
                      you leave out simply does not appear.
                      {' '}<strong>not set</strong> renders as nothing — the sentence around it
                      still appears, saying less than you meant.
                      {' '}A <strong>placeholder</strong> value like “Your Company” is what the AI
                      says when you have not told it otherwise.
                      {' '}<strong>per account</strong> is filled in when the prompt runs.
                    </p>
                  </div>
                )}

                <textarea
                  className="pe-textarea"
                  value={draft}
                  spellCheck={false}
                  onChange={e => setDraft(e.target.value)}
                  rows={22}
                />

                {saveError && <div className="pe-error">{saveError}</div>}

                <div className="pe-editor-actions">
                  <button type="button" className="pe-btn pe-btn--primary" disabled={!dirty || saving} onClick={save}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" className="pe-btn pe-btn--ghost" disabled={saving} onClick={() => setDraft(p.defaultBody)}>
                    Load the default
                  </button>
                  <span className="pe-count">{draft.length.toLocaleString()} characters</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
